import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
    FaCheck,
    FaTimes,
    FaSearch,
    FaEye,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaPlus,
    FaEdit,
    FaSave,
    FaPause,
    FaTrash,
    FaLink,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const resolveCompanyLogoUrl = (application, userId) => {
    const generatedUrl = application?.company_logo_url;
    const storedPath = application?.company_logo;

    if(generatedUrl) return generatedUrl;
    if(!storedPath) return "";
    if(storedPath.startsWith("http://") || storedPath.startsWith("https://")) {
        return storedPath;
    }
    if(storedPath.startsWith("/uploads/")) {
        return `${process.env.REACT_APP_BACKEND_URL}${storedPath}`;
    }
    if(storedPath.startsWith("uploads/")) {
        return `${process.env.REACT_APP_BACKEND_URL}/${storedPath}`;
    }
    if(storedPath.includes("/")) {
        return `${process.env.REACT_APP_BACKEND_URL}/uploads/${storedPath}`;
    }

    return `${process.env.REACT_APP_BACKEND_URL}/uploads/${userId}/logo/${storedPath}`;
};

const AccountApprovalDashboard = ({ user }) => {
    const [applications, setApplications] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [roleFilter, setRoleFilter] = useState("investor");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showActionsDropdown, setShowActionsDropdown] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [showAdditionalModal, setShowAdditionalModal] = useState(false);
    const [additionalFiles, setAdditionalFiles] = useState([]);
    const [selectedAdditionalUserId, setSelectedAdditionalUserId] =
        useState(null);
    const [selectedAdditionalRole, setSelectedAdditionalRole] = useState(null);
    const [, setAdditionalApproved] = useState(false);
    const [rejectionNote, setRejectionNote] = useState("");
    const [showNoteBox, setShowNoteBox] = useState(false);
    const [internalNote, setInternalNote] = useState("");
    const [internalNotes, setInternalNotes] = useState([]);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState("");
    const [previewFileUrl, setPreviewFileUrl] = useState(null);
    const [previewFileType, setPreviewFileType] = useState(null);
    // On Hold state
    const [showOnHoldBox, setShowOnHoldBox] = useState(false);
    const [onHoldNote, setOnHoldNote] = useState("");
    const [displayedOnHoldNote, setDisplayedOnHoldNote] = useState("");
    // Assign NC IDs state
    const [showNCForm, setShowNCForm] = useState(false);
    const [ncIssuerId, setNcIssuerId] = useState("");
    const [ncOfferingId, setNcOfferingId] = useState("");
    const [ncPartyId, setNcPartyId] = useState("");
    const [ncAccountId, setNcAccountId] = useState("");
    const [ncSaving, setNcSaving] = useState(false);

    const signatureUrl = (path) =>
        `${process.env.REACT_APP_BACKEND_URL}/${path.replace(/^\/?/, "")}`;

    const normalizeRole = (role) =>
        (role || "").toString().trim().toLowerCase();

    const filteredApplicationRows = useMemo(() => {
        const lower = searchTerm.trim().toLowerCase();
        const activeRole = normalizeRole(roleFilter);

        return applications.filter((app) => {
            const appRole = normalizeRole(app.role);
            const fullName = `${app.first_name || ""} ${app.last_name || ""}`
                .trim()
                .toLowerCase();

            const matchesSearch =
                !lower ||
                fullName.includes(lower) ||
                (app.email || "").toString().toLowerCase().includes(lower) ||
                appRole.includes(lower);

            const matchesRole =
                activeRole === "all" || appRole === activeRole;

            let matchesStatus = true;
            if(statusFilter !== "all") {
                if(statusFilter === "deleted") {
                    matchesStatus = app.is_deleted === 1;
                } else {
                    const appStatus = (app.status || "")
                        .toString()
                        .trim()
                        .toLowerCase();
                    matchesStatus =
                        appStatus === statusFilter.toLowerCase() &&
                        app.is_deleted !== 1;
                }
            }

            return matchesSearch && matchesRole && matchesStatus;
        });
    }, [applications, searchTerm, roleFilter, statusFilter]);

    useEffect(() => {
        fetchPendingApplications();
        // Call admin warning API for sandbox NC records in investors/applications
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/sandbox-nc-record-count-applications`, { withCredentials: true })
            .then((res) => {
                if (res.data && res.data.count > 0) {
                    notifyUser(null, "warning", `Warning: There are ${res.data.count} active investors/entrepreneurs with nc_record_status set to 'sandbox' while the North Capital API is in production mode. Please review and update these campaigns to 'production' to avoid issues.`, "toast", null, null, 10000, null, null, false, false, "top-center");
                }
            })
            .catch((err) => {
                // Optionally log error
                // console.error('Failed to fetch sandbox NC record count for applications/investors', err);
            });
    }, []);

    const openAdditionalModal = async (userId, role) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${userId}/additional-uploads?userRole=${role}`,
                { withCredentials: true },
            );
            setAdditionalFiles(response.data.additionalUploads || []);
            setAdditionalApproved(response.data.approved || false);
            setSelectedAdditionalUserId(userId);
            setSelectedAdditionalRole(role);
            setShowAdditionalModal(true);
        } catch(error) {
            console.error("Failed to fetch additional uploads:", error);
            notifyUser(null, "error", "Error fetching additional uploads", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const rejectAdditionalUploads = () => {
        // Reject all additional uploads (batch operation)
        const fileNames = additionalFiles
            .map((file) =>
                typeof file === "string"
                    ? file.split("/").pop()
                    : file.name || (file.path && file.path.split("/").pop()),)
            .filter((name) => name);

        if(fileNames.length === 0) {
            notifyUser(null, "error", "No files found to reject", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Use the first file name for the email, but indicate it's for all uploads
        const fileName =
            fileNames.length === 1
                ? fileNames[0]
                : `${fileNames.length} additional files`;

        axios
            .put(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/approve-additional-uploads`,
                {
                    userId: selectedAdditionalUserId,
                    role: selectedAdditionalRole,
                    fileName,
                    status: false,
                    rejectionNote,
                },
                { withCredentials: true },
            )
            .then(() => {
                notifyUser(null, "success", "All additional uploads rejected", "toast", null, null, null, null, null, false, false, "top-center");
                setShowAdditionalModal(false);
            })
            .catch((error) => {
                console.error("Rejection failed:", error);
                notifyUser(null, "error", "Failed to reject additional uploads", "toast", null, null, null, null, null, false, false, "top-center");
            });
    };

    const approveAdditionalUploads = async (userId) => {
        try {
            // Approve all additional uploads (batch operation)
            const fileNames = additionalFiles
                .map((file) =>
                    typeof file === "string"
                        ? file.split("/").pop()
                        : file.name ||
                          (file.path && file.path.split("/").pop()),)
                .filter((name) => name);

            if(fileNames.length === 0) {
                notifyUser(null, "error", "No files found to approve", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            // Use the first file name for the email, but indicate it's for all uploads
            const fileName =
                fileNames.length === 1
                    ? fileNames[0]
                    : `${fileNames.length} additional files`;

            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/approve-additional-uploads`,
                {
                    userId,
                    role: selectedAdditionalRole,
                    fileName,
                    status: true,
                },
                { withCredentials: true },
            );
            notifyUser(null, "success", "All additional uploads approved", "toast", null, null, null, null, null, false, false, "top-center");
            fetchPendingApplications();
            setShowAdditionalModal(false);
        } catch(error) {
            console.error("Approval failed:", error);
            notifyUser(null, "error", "Failed to approve additional uploads", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const fetchApplicationDetails = async (
        userId,
        role,
        has_additional_uploads,
    ) => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/application-details/${userId}/${role}`,
                { withCredentials: true },
            );
            console.log("Application details fetched:", data);

            setModalData(data);
            setSelectedApp({ id: userId, role, has_additional_uploads });
            setShowModal(true);
            // Ensure NC form is closed when opening details so inputs don't persist
            setShowNCForm(false);

            // Prefill NC IDs from returned data so the Assign NC IDs form shows existing values immediately
            if (role === "investor") {
                setNcPartyId(data?.details?.north_capital_party_id || "");
                setNcAccountId(data?.details?.north_capital_account_id || "");
            } else if (role === "entrepreneur") {
                setNcIssuerId(data?.campaign?.north_capital_issuer_id || data?.application?.north_capital_issuer_id || "");
                setNcOfferingId(data?.campaign?.north_capital_offering_id || data?.application?.north_capital_offering_id || "");
            }

            // Set displayed on-hold note based on role and data structure
            let status, onHoldNote;

            if(role === "investor") {
                status = data.details?.status;
                onHoldNote = data.details?.on_hold_note;
            } else if(role === "entrepreneur") {
                status = data.application?.status;
                onHoldNote = data.application?.on_hold_note;
            }

            console.log("Extracted status and note:", { status, onHoldNote });

            if(status === "onhold" && onHoldNote) {
                console.log("Setting on-hold note:", onHoldNote);
                setDisplayedOnHoldNote(onHoldNote);
            } else {
                console.log("No on-hold note found or status not onhold");
                setDisplayedOnHoldNote("");
            }

            // Fetch internal notes
            fetchInternalNotes(role, data);
        } catch{
            notifyUser(null, "error", "Failed to fetch user details", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const fetchPendingApplications = async () => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/all-applications`,
                { withCredentials: true },
            );
            setApplications(sortByAuditUpdatedOn(data));
        } catch{
            notifyUser(null, "error", "Failed to load applications", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const getEmbeddedUrl = (url) => {
        if(!url) return "";

        try {
            const parsedUrl = new URL(url);

            if(parsedUrl.hostname.includes("youtube.com")) {
                // Handle live URLs
                if(parsedUrl.pathname.includes("/live/")) {
                    const videoId = parsedUrl.pathname.split("/").pop();
                    return `https://www.youtube.com/embed/${videoId}`;
                }

                // Handle standard watch URLs
                const videoId = parsedUrl.searchParams.get("v");
                return `https://www.youtube.com/embed/${videoId}`;
            }

            if(parsedUrl.hostname.includes("youtu.be")) {
                const videoId = parsedUrl.pathname.split("/").pop();
                return `https://www.youtube.com/embed/${videoId}`;
            }

            if(parsedUrl.hostname.includes("vimeo.com")) {
                const videoId = parsedUrl.pathname.split("/").pop();
                return `https://player.vimeo.com/video/${videoId}`;
            }

            return url;
        } catch{
            console.error("Invalid video URL:", url);
            return "";
        }
    };

    // create internal notes
    const handleSubmitInternalNote = async () => {
        if(!internalNote.trim()) {
            notifyUser(null, "error", "Please enter a note before saving.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            const userId = user?.id;
            const role = selectedApp?.role || modalData?.user?.role;

            let payload = {
                role,
                note: internalNote,
                createdByUserId: userId,
            };

            if(role === "investor") {
                payload.investorId = modalData.details?.id;
            } else if(role === "entrepreneur") {
                payload.applicationId = modalData.application?.id;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/add-note`,
                payload,
                { withCredentials: true },
            );

            if(response.status === 200) {
                notifyUser(null, "success", "Note saved successfully.", "toast", null, null, null, null, null, false, false, "top-center");
                setInternalNote("");
                setShowNoteBox(false);
                // Re-fetch internal notes so the new note appears immediately
                fetchInternalNotes(role, modalData);
            } else {
                notifyUser(null, "error", "Failed to save note.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            console.error("Error saving note:", error);
            notifyUser(null, "error", "An error occurred while saving the note.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // fetch internal notes
    const fetchInternalNotes = async (role, modalData) => {
        let id = null;
        if(role === "investor") {
            id = modalData?.details?.id;
        } else if(role === "entrepreneur") {
            id = modalData?.application?.id;
        }
        if(!id) {
            setInternalNotes([]);
            return;
        }
        try {
            const notesRes = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/internal-notes?role=${role}&id=${id}`,
                { withCredentials: true },
            );
            setInternalNotes(notesRes.data.notes || []);
        } catch{
            setInternalNotes([]);
        }
    };

    // Edit internal note function
    const handleEditNote = (noteId, currentText) => {
        setEditingNoteId(noteId);
        setEditingNoteText(currentText);
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingNoteText("");
    };

    // Save edited note
    const handleSaveEditedNote = async (noteId) => {
        if(!editingNoteText.trim()) {
            notifyUser(null, "error", "Please enter a note before saving.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-note/${noteId}`,
                {
                    note: editingNoteText,
                },
                { withCredentials: true },
            );

            if(response.status === 200) {
                notifyUser(null, "success", "Note updated successfully.", "toast", null, null, null, null, null, false, false, "top-center");
                setEditingNoteId(null);
                setEditingNoteText("");
                // Re-fetch internal notes to show updated content
                const role = selectedApp?.role || modalData?.user?.role;
                fetchInternalNotes(role, modalData);
            } else {
                notifyUser(null, "error", "Failed to update note.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            console.error("Error updating note:", error);
            console.error("Error response:", error.response?.data);
            console.error("Error status:", error.response?.status);

            if(error.response?.data?.error) {
                notifyUser(null, "error", `Error: ${error.response.data.error}`, "toast", null, null, null, null, null, false, false, "top-center");
            } else if(error.response?.status === 401) {
                notifyUser(null, "error", "Authentication failed. Please log in again.", "toast", null, null, null, null, null, false, false, "top-center");
            } else if(error.response?.status === 403) {
                notifyUser(null, "error", "Access denied. Admin privileges required.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "An error occurred while updating the note.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleVerification = async (
        userId,
        role,
        status,
        closeModal,
        note = "",
        onHold = false,
        onHoldNoteVal = "",
        submitToNC = false,
    ) => {

        try {
            let payload = {
                userId,
                role,
                status,
                rejectionNote: note,
            };
            // Optional flag to instruct the backend to submit data to North Capital (NC)
            if (submitToNC) payload.submitToNC = true;
            if(onHold) {
                payload.onHold = true;
                payload.onHoldNote = onHoldNoteVal;

                // Set the displayed on-hold note when putting on hold
                setDisplayedOnHoldNote(onHoldNoteVal);
                console.log(
                    "Setting on-hold note in handleVerification:",
                    onHoldNoteVal,
                );
            }

            // === ENTREPRENEUR APPROVAL: Only approve if issuer and account created ===
            if(role === "entrepreneur" && status === true && !onHold) {
                let issuerId = null;
                try {
                    // 1. Create Issuer
                    const applicationId = modalData?.application?.id;
                    const issuerPayload = {
                        issuerName: modalData?.application?.company_name,
                        firstName: modalData?.user?.first_name,
                        lastName: modalData?.user?.last_name,
                        phoneNumber: modalData?.user?.phone_number,
                        email: modalData?.user?.email,
                        applicationId,
                    };
                    console.log("[DEBUG] Issuer Payload:", issuerPayload);
                    const issuerRes = await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/issuer/create-issuer`,
                        issuerPayload,
                        { withCredentials: true },
                    );
                    issuerId = issuerRes.data?.data?.issuerId || issuerRes.data?.data?.issuer_id;
                    if(!issuerId) {
                        notifyUser(null, "error", "Issuer ID missing, could not create issuer account", "toast", null, null, null, null, null, false, false, "top-center");
                        throw new Error("Issuer ID missing");
                    }
                    // 2. Create Issuer Account
                    const accountPayload = {
                        issuerId,
                        accountType: "Checking",
                        companyName: modalData?.application?.company_name,
                        companyState: modalData?.application?.state ,
                        entityType: modalData?.application?.entity_type || "N/A",
                        companyTaxID: modalData?.application?.company_tax_id || "",
                        accountMiddleInitial: modalData?.application?.middle_initial || "N/A",
                        socialSecurityNumber: modalData?.application?.ssn || "N/A",
                        dob: modalData?.application?.dob || "",
                        residentUS: "yes",
                        citizenUS: "yes",
                        addressline1: modalData?.application?.address1,
                        addressline2: modalData?.application?.address2 ,
                        city: modalData?.application?.city,
                        zip: modalData?.application?.zip_code,
                        country: modalData?.application?.country || "USA",
                        issuingCountry: "USA",
                        additionalInfo: "Entrepreneur auto-approved",
                    };
                    console.log("[DEBUG] Account Payload:", accountPayload);
                    await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/api/issuer/create-issuer-account`,
                        accountPayload,
                        { withCredentials: true },
                    );
                } catch(err) {
                    notifyUser(null, "error", "Failed to create issuer or issuer account. Application will remain pending.", "toast", null, null, null, null, null, false, false, "top-center");
                    console.error("Issuer creation error:", err);
                    // Do not approve, leave as pending
                    return;
                }
            }

            // Now update verification status (for all other cases, or after successful issuer/account creation)
            console.log("Sending payload to backend:", payload);
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/update-verification`,
                payload,
                { withCredentials: true },
            );
            console.log("Backend response:", res.data);

            if(onHold) {
                notifyUser(null, "info", "Application put on hold and feedback saved.", "toast", null, null, null, null, null, false, false, "top-center");
                // Directly update the displayed on-hold note
                setDisplayedOnHoldNote(onHoldNoteVal);

                // Update modalData to reflect the on-hold status based on role
                if(modalData && role) {
                    if(role === "investor" && modalData.details) {
                        const updatedDetails = {
                            ...modalData.details,
                            status: "onhold",
                            on_hold_note: onHoldNoteVal,
                        };
                        setModalData({
                            ...modalData,
                            details: updatedDetails,
                        });
                    } else if(
                        role === "entrepreneur" &&
                        modalData.application
                    ) {
                        const updatedApplication = {
                            ...modalData.application,
                            status: "onhold",
                            on_hold_note: onHoldNoteVal,
                        };
                        setModalData({
                            ...modalData,
                            application: updatedApplication,
                        });
                    }
                }
            } else {
                // --- Custom logic for investor approval and NC KYC ---
                if (role === "investor" && status === true) {
                    if (res.data && res.data.autoApproved === false) {
                        notifyUser(null, "warning", res.data.kycStatus
                                ? `North Capital KYC failed or is pending: ${res.data.kycStatus}. Application is still pending.`
                                : "North Capital KYC failed or is pending. Application is still pending.", "toast", null, null, null, null, null, false, false, "top-center");
                    } else if (res.data && res.data.autoApproved === true) {
                        notifyUser(null, "success", "Application approved successfully", "toast", null, null, null, null, null, false, false, "top-center");
                    } else {
                       notifyUser(null, "error", "Please try again later.", "toast", null, null, null, null, null, false, false, "top-center");
                    }
                } else {
                    notifyUser(null, "success", `Application ${
                            status ? "approved" : "rejected"
                        } successfully`, "toast", null, null, null, null, null, false, false, "top-center");
                }
            }
            fetchPendingApplications();
            if(closeModal) {
                closeModal();
                setSelectedApp(null);
                setModalData(null);
            }
            setShowOnHoldBox(false);
            setOnHoldNote("");
        } catch(error) {
            if(
                error.response &&
                error.response.data &&
                error.response.data.error
            ) {
                notifyUser(null, "error", error.response.data.error, "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to update application status", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleAssignNCIds = async () => {
        setNcSaving(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/assign-nc-ids`,
                {
                    userId: selectedApp.id,
                    role: selectedApp.role,
                    issuerId: ncIssuerId || undefined,
                    offeringId: ncOfferingId || undefined,
                    partyId: ncPartyId || undefined,
                    accountId: ncAccountId || undefined,
                },
                { withCredentials: true },
            );
            notifyUser(null, "success", "NC IDs saved successfully", "toast", null, null, null, null, null, false, false, "top-center");
            setShowNCForm(false);
            fetchApplicationDetails(selectedApp.id, selectedApp.role);
        } catch (err) {
            notifyUser(null, "error", "Failed to save NC IDs", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setNcSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        const isEntrepreneur = selectedApp?.role === "entrepreneur";
        const result = await Swal.fire({
            title: "Delete Account",
            text: isEntrepreneur
                ? "Are you sure you want to delete this account? This actions delete issuer in north capital."
                : "Are you sure you want to delete this account?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            customClass: {
                confirmButton: "swal-confirm-reject",
                cancelButton: "swal-cancel",
                actions: "swal-actions",
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/delete-account`,
                { userId: selectedApp.id, role: selectedApp.role },
                { withCredentials: true },
            );
            notifyUser(null, "success", "Account deleted successfully", "toast", null, null, null, null, null, false, false, "top-center");
            setShowModal(false);
            setSelectedApp(null);
            setModalData(null);
            fetchPendingApplications();
        } catch (error) {
            notifyUser(null, "error", error.response?.data?.error || "Failed to delete account", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleDisableAccount = async () => {
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/disable-action`,
                {
                    userId: selectedApp.id,
                    role: selectedApp.role,
                },
                { withCredentials: true },
            );
            notifyUser(null, "success", "Account disabled successfully.", "toast", null, null, null, null, null, false, false, "top-center");
            setShowActionsDropdown(false);
            fetchPendingApplications();
            setShowModal(false);
        } catch {
            notifyUser(null, "error", "Failed to disable account.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Go Live: submit application data to NC (North Capital)
    const handleGoLive = async () => {
        const result = await Swal.fire({
            title: "Go Live",
            text: "Clicking Go Live will submit this user's data to North Capital. Continue?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Continue",
            cancelButtonText: "Cancel",
            customClass: {
                confirmButton: "swal-confirm-approve",
                cancelButton: "swal-cancel",
                actions: "swal-actions",
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;
        try {
            // Build issuer payload from modalData
            const applicationId = modalData?.application?.id || null;
            const issuerPayload = {
                issuerName:
                    modalData?.application?.company_name ||
                    modalData?.entrepreneur?.company_name ||
                    "",
                firstName: modalData?.user?.first_name || "",
                lastName: modalData?.user?.last_name || "",
                phoneNumber: modalData?.user?.phone_number || "",
                email: modalData?.user?.email || "",
                applicationId,
            };

            const issuerRes = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/issuer/create-issuer`,
                issuerPayload,
                { withCredentials: true },
            );

            const issuerId =
                issuerRes?.data?.data?.issuerId || issuerRes?.data?.data?.issuer_id ||
                issuerRes?.data?.data?.issuerId;

            if (!issuerId) {
                console.error("create-issuer response:", issuerRes?.data);
                throw new Error("Issuer creation did not return an issuerId");
            }

            // Build issuer account payload
            const app = modalData?.application || {};
            const accountPayload = {
                issuerId,
                accountType: "Checking",
                companyName: app.company_name || "",
                companyState: app.state || app.company_state || "",
                entityType: app.entity_type || "N/A",
                companyTaxID: app.company_tax_id || "",
                accountMiddleInitial: app.middle_initial || "N/A",
                socialSecurityNumber: app.ssn || app.social_security_number || "",
                dob: app.dob || "",
                residentUS: "yes",
                citizenUS: "yes",
                addressline1: app.address1 || app.address || "",
                addressline2: app.address2 || "",
                city: app.city || "",
                zip: app.zip_code || app.zip || "",
                country: app.country || "USA",
                issuingCountry: "USA",
                additionalInfo: "Go Live initiated from admin dashboard",
            };

            const accountRes = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/issuer/create-issuer-account`,
                accountPayload,
                { withCredentials: true },
            );

            if (!(accountRes?.data && accountRes.data.data && accountRes.data.data.success !== false)) {
                console.warn("create-issuer-account response:", accountRes?.data);
            }

            notifyUser(null, "success", "Go Live: Issuer and Issuer Account created in NC successfully.", "toast", null, null, null, null, null, false, false, "top-center");

            // Optionally mark application as having NC production record locally in UI
            setModalData((prev) => {
                if (!prev) return prev;
                if (prev.application) {
                    return {
                        ...prev,
                        application: {
                            ...prev.application,
                            nc_record_status: "production",
                        },
                    };
                }
                if (prev.details) {
                    return {
                        ...prev,
                        details: {
                            ...prev.details,
                            nc_record_status: "production",
                        },
                    };
                }
                return prev;
            });

            fetchPendingApplications();
            setShowModal(false);
        } catch (err) {
            console.error("Go Live failed:", err);
            const message = err.response?.data?.error || err.message || "Failed to Go Live. Please try again.";
            notifyUser(null, "error", message, "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleEnableAccount = async () => {
        const result = await Swal.fire({
            title: "Enable Account",
            text: "This will re-enable the account and restore its previous status.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Enable",
            cancelButtonText: "Cancel",
            customClass: {
                confirmButton: "swal-confirm-approve",
                cancelButton: "swal-cancel",
                actions: "swal-actions",
            },
            buttonsStyling: false,
        });

        if(!result.isConfirmed) return;

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/account-approval/enable-action`,
                { userId: selectedApp.id, role: selectedApp.role },
                { withCredentials: true },
            );

            const restoredStatus = response.data?.status || "pending";

            setModalData((prev) => {
                if(!prev) return prev;

                if(selectedApp?.role === "investor" && prev.details) {
                    return {
                        ...prev,
                        details: {
                            ...prev.details,
                            is_disabled: 0,
                            status: restoredStatus,
                        },
                    };
                }

                if(selectedApp?.role === "entrepreneur" && prev.application) {
                    return {
                        ...prev,
                        application: {
                            ...prev.application,
                            is_disabled: 0,
                            status: restoredStatus,
                        },
                    };
                }

                return prev;
            });

            notifyUser(null, "success", `Account enabled. Status restored to ${restoredStatus}.`, "toast", null, null, null, null, null, false, false, "top-center");
            fetchPendingApplications();
        } catch(error) {
            notifyUser(null, "error", error.response?.data?.error || "Failed to enable account.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const confirmAction = (actionType, closeModal) => {
        if(actionType === "reject") {
            Swal.fire({
                title: "Reject Application",
                input: "textarea",
                inputLabel: "Rejection Note (optional)",
                inputPlaceholder: "Enter the reason for rejection...",
                inputAttributes: {
                    "aria-label": "Rejection note",
                },
                showCancelButton: true,
                confirmButtonText: "Reject",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "swal-confirm-reject",
                    cancelButton: "swal-cancel",
                    actions: "swal-actions",
                },
                buttonsStyling: false,
            }).then((result) => {
                if(result.isConfirmed) {
                    const note = result.value || "";
                    setRejectionNote(note);
                    handleVerification(
                        selectedApp.id,
                        selectedApp.role,
                        false,
                        closeModal,
                        note,
                    );
                    // If modal should remain open, refresh the data to ensure consistent UI
                    if(!closeModal) {
                        fetchApplicationDetails(
                            selectedApp.id,
                            selectedApp.role,
                        );
                    }
                }
            });
        } else if(actionType === "onHold") {
            setShowOnHoldBox(true);
            return;
        } else {
            Swal.fire({
                title: `Are you sure you want to approve this application?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Approve!",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "swal-confirm-approve",
                    cancelButton: "swal-cancel",
                    actions: "swal-actions",
                },
                buttonsStyling: false,
            }).then((result) => {
                if(result.isConfirmed) {
                    handleVerification(
                        selectedApp.id,
                        selectedApp.role,
                        true,
                        closeModal,
                    );
                    if(!closeModal) {
                        fetchApplicationDetails(
                            selectedApp.id,
                            selectedApp.role,
                        );
                    }
                }
            });
        }
    };

    const handleExportCSV = () => {
        const exportList = filteredApplicationRows;

        if(!exportList.length) {
            Swal.fire({ icon: "info", title: "No data to export" });
            return;
        }

        const rows = exportList.map((s) => ({
            NAME: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
            EMAIL: s.email || "",
            USERTYPE: s.role || "",
            STATUS: s.is_deleted === 1 ? "deleted" : s.status || "",
            NC_RECORD_STATUS: s.nc_record_status || "",
            SUBMITTED_AT: s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "",
        }));

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "applications_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    // Inline styles for SweetAlert2 buttons
    const swalStyles = `
    .swal-confirm-approve {
      background-color: var(--navy) !important;
      color: #C2D7E2 !important;
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      font-weight: bold;
      cursor: pointer;
    }
    .swal-confirm-reject {
      background-color: #C2D7E2 !important;
      color: var(--navy) !important;
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      font-weight: bold;
      cursor: pointer;
    }
    .swal-cancel {
      background-color: #C2D7E2 !important;
      color: var(--navy) !important;
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      font-weight: bold;
      cursor: pointer;
    }
    .swal-actions {
      display: flex;
      justify-content: center;
      gap: 10px; /* Add gap between buttons */
    }
  `;

    // Inject styles into the document head
    useEffect(() => {
        const styleTag = document.createElement("style");
        styleTag.innerHTML = swalStyles;
        document.head.appendChild(styleTag);

        return () => {
            document.head.removeChild(styleTag);
        };
    }, []);

    const sortedApplicationRows = useMemo(() => {
        return [...filteredApplicationRows].sort((a, b) => {
            const aDate = a.submitted_at
                ? new Date(a.submitted_at)
                : new Date(0);
            const bDate = b.submitted_at
                ? new Date(b.submitted_at)
                : new Date(0);
            return bDate - aDate;
        });
    }, [filteredApplicationRows]);

    const filteredTotal = sortedApplicationRows.length;

    const paginatedApplications = useMemo(() => {
        const start = (currentPage - 1) * perPage;
        return sortedApplicationRows.slice(start, start + perPage);
    }, [sortedApplicationRows, currentPage, perPage]);

    const totalPages = Math.max(1, Math.ceil(filteredTotal / perPage));

    const renderNcStatus = (status) => {
        const displayStatus = status || "-";
        const normalizedStatus = displayStatus.toString().toLowerCase();
        let statusClass = "";

        if(normalizedStatus.includes("auto approved")) {
            statusClass = "text-green-600 font-semibold";
        } else if(
            normalizedStatus.includes("disapproved") ||
            normalizedStatus.includes("not approved") ||
            normalizedStatus.includes("failed")
        ) {
            statusClass = "text-red-600 font-semibold";
        }

        return <span className={statusClass}>{displayStatus}</span>;
    };

    const columns = [
        {
            name: "NAME",
            selector: (row) => `${row.first_name} ${row.last_name}`,
            sortable: true,
        },
        {
            name: "EMAIL",
            selector: (row) => row.email,
            sortable: true,
        },
        {
            name: "USER TYPE",
            selector: (row) => row.role,
            sortable: true,
        },
        {
            name: "AML STATUS",
            selector: (row) => row.north_capital_aml_status || "-",
            sortable: true,
            omit: roleFilter !== "investor",
            cell: (row) =>
                renderNcStatus(row.north_capital_aml_status),
        },
        {
            name: "KYC STATUS",
            selector: (row) => row.north_capital_kyc_status || "-",
            sortable: true,
            omit: roleFilter !== "investor",
            cell: (row) =>
                renderNcStatus(row.north_capital_kyc_status),
        },
        {
            name: "STATUS",
            selector: (row) => row.status,
            sortable: true,
                cell: (row) => (
                <span
                    className={
                        row.is_disabled === 1
                            ? "text-yellow-600 font-semibold"
                            : row.is_deleted === 1
                                ? "text-red-700 font-semibold"
                                : row.status === "approved"
                                    ? "text-green-600 font-semibold"
                                    : row.status === "rejected"
                                        ? "text-red-500 font-semibold"
                                        : "text-yellow-600 font-semibold"
                    }
                >
                    {row.is_disabled === 1 ? "disabled" : row.is_deleted === 1 ? "deleted" : row.status}
                </span>
            ),
        },
        {
            name: "NC Record Status",
            selector: (row) => row.nc_record_status || "-",
            sortable: true,
            cell: (row) => {
                // Only highlight 'production' (green) and 'sandbox' (yellow)
                const status = row.nc_record_status || "-";
                let statusClass = "";
                if(status.toLowerCase() === "production") {
                    statusClass = "text-green-600 font-semibold";
                } else if(status.toLowerCase() === "sandbox") {
                    statusClass = "text-yellow-600 font-semibold";
                }
                return <span className={statusClass}>{status}</span>;
            },
        },
        {
            name: "ADDITIONAL UPLOADS",
            selector: (row) => row.has_additional_uploads,
            sortable: true,
            cell: (row) => {
                if(row.has_additional_uploads) {
                    // Show "Pending Review" if has_additional_uploads is true
                    return (
                        <div className="flex items-center justify-center">
                            <span
                                className="px-2 py-1 rounded-full text-xs font-semibold flex items-center cursor-pointer bg-orange-100 text-orange-800"
                                onClick={() =>
                                    openAdditionalModal(row.id, row.role)
                                }
                            >
                                <FaEdit className="mr-1" />
                                Pending Review
                            </span>
                        </div>
                    );
                } else {
                    return (
                        <div className="flex items-center justify-center">
                            <span>-</span>
                        </div>
                    );
                }
            },
        },
        {
            name: "SUBMITTED AT",
            selector: (row) =>
                row.submitted_at
                    ? new Date(row.submitted_at).toLocaleString()
                    : "-",
            sortable: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "13px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "45px",
                "&:hover": { backgroundColor: "#f1f8ff" },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    return (
        <div
            className="aqi-grid-page font-poppins"
            style={{
                padding: "30px",
                width: "90%",
                minHeight: "80vh",
                height: "auto",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
                overflowX: "hidden",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                APPLICATIONS
            </h2>

            {selectedApp && (
                <div className="flex justify-end gap-2 mb-4">
                    {/* View Additional Uploads Button */}
                    {!!selectedApp.has_additional_uploads && (
                        <button
                            className="flex items-center border border-indigo-500 text-indigo-500 px-3 py-1.5 text-sm rounded hover:bg-indigo-600 hover:text-white"
                            onClick={() =>
                                openAdditionalModal(
                                    selectedApp.id,
                                    selectedApp.role,
                                )
                            }
                        >
                            <FaEye className="mr-1" /> Additional Uploads
                        </button>
                    )}
                </div>
            )}

            <div className="flex gap-4 mb-6 justify-center border-b border-[#C2D7E2] pb-4">
                {[
                    { label: "Investors", value: "investor" },
                    { label: "Entrepreneurs", value: "entrepreneur" },
                ].map((tab) => (
                    <button
                        key={tab.value}
                        type="button"
                        className={`px-4 py-2 rounded font-semibold transition ${
                            roleFilter === tab.value
                                ? "bg-navy text-white"
                                : "bg-gray-100 text-navy"
                        }`}
                        onClick={() => {
                            setRoleFilter(tab.value);
                            setCurrentPage(1);
                            setSelectedApp(null);
                            setModalData(null);
                            setShowModal(false);
                            setShowActionsDropdown(false);
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Filters Section */}
            <div className="aqi-grid-toolbar">
                {/* Rows per page */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                {/* Status filter dropdown */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                            minWidth: "200px",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="onhold">On Hold</option>
                        <option value="deleted">Deleted</option>
                    </select>
                </div>
                <div className="aqi-grid-search">
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search applications..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                        }}
                        className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    />
                </div>

                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            {/* DataTable */}
            <DataTable
                key={`${roleFilter}-${statusFilter}-${searchTerm}-${currentPage}-${perPage}`}
                columns={columns}
                data={paginatedApplications}
                responsive
                noTableHead={false}
                customStyles={{
                    ...customStyles,
                    table: {
                        style: {
                            borderRadius: "8px",
                            width: "100%",
                            overflowX: "hidden",
                        },
                    },
                    rows: {
                        style: {
                            cursor: "pointer",
                            minHeight: "50px",
                            "&:hover": {
                                backgroundColor: "#E9F4F9",
                                borderLeft: "2px solid var(--navy)",
                            },
                        },
                    },
                }}
                conditionalRowStyles={[
                    {
                        // Selected row
                        when: (row) => selectedApp && row.id === selectedApp.id,
                        style: {
                            backgroundColor: "#e6f7ff",
                            borderLeft: "4px solid var(--navy)",
                            color: "var(--navy)",
                            fontWeight: "500",
                            boxShadow: "0 0 5px rgb(var(--navy-rgb) / 0.2)",
                            transform: "translateX(2px)",
                            "&:hover": {
                                backgroundColor: "#e6f7ff",
                                borderLeft: "4px solid var(--navy)",
                            },
                        },
                    },
                ]}
                striped
                pointerOnHover
                pagination={false}
                onRowClicked={(row) => {
                    // Open the modal with the clicked record's details
                    if(row && row.id && row.role) {
                        fetchApplicationDetails(
                            row.id,
                            row.role,
                            row.has_additional_uploads,
                        );
                    } else {
                        setSelectedApp(
                            row.id
                                ? {
                                      id: row.id,
                                      role: row.role,
                                      has_additional_uploads:
                                          row.has_additional_uploads,
                                  }
                                : row,
                        );
                    }
                }}
            />

            {/* Pagination */}
            <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                <span>
                    Showing{" "}
                    {filteredTotal === 0
                        ? 0
                        : (currentPage - 1) * perPage + 1}{" "}
                    to {Math.min(currentPage * perPage, filteredTotal)} of{" "}
                    {filteredTotal} records
                </span>
                <div className="flex gap-2 text-lg items-center">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleLeft />
                    </button>
                    <button
                        disabled={currentPage === 1}
                        onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleLeft />
                    </button>
                    <span className="text-sm px-2">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() =>
                            setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages),)
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleRight />
                    </button>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleRight />
                    </button>
                </div>

                {/* View Modal */}
                {showModal && modalData && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-lg w-11/12 max-w-5xl p-6 relative max-h-[90vh] overflow-y-auto">
                            <h3 className="text-xl font-bold mb-4 text-navy">
                                Application Details
                            </h3>
                            {/* Modal Approve/Reject Buttons - visible depending on application status */}
                            <div className="flex justify-end gap-2 mb-4">
                                {/** Determine application status safely from modalData based on role **/}
                                {(() => {
                                    const role = selectedApp?.role;
                                    const appStatus =
                                        role === "investor"
                                            ? modalData?.details?.status
                                            : modalData?.application?.status;
                                    const isDeleted =
                                        role === "investor"
                                            ? modalData?.details?.is_deleted === 1
                                            : modalData?.entrepreneur?.is_deleted === 1;
                                    const isDisabled =
                                        (role === "investor" && (modalData?.details?.is_disabled === 1 || modalData?.details?.status === "disabled")) ||
                                        (role === "entrepreneur" && (modalData?.application?.is_disabled === 1 || modalData?.application?.status === "disabled"));

                                    // Requested delete handling stays as-is
                                    const deleteRequested =
                                        modalData?.user?.delete_requested === 1 ||
                                        applications.find((a) => (a.id === selectedApp?.id || a.user_id === selectedApp?.id || a.userId === selectedApp?.id))?.delete_requested === 1;
                                    if (deleteRequested) {
                                        return (
                                            <>
                                                <button
                                                    className="flex items-center border border-red-600 text-red-600 px-3 py-1.5 text-sm rounded hover:bg-red-600 hover:text-white"
                                                    onClick={async () => {
                                                        try {
                                                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/account-approval/deletion-requests/${selectedApp.id}/decision`, { approve: false }, { withCredentials: true });
                                                            notifyUser(null, "success", 'Delete request rejected', "toast", null, null, null, null, null, false, false, "top-center");
                                                            fetchPendingApplications();
                                                            setShowModal(false);
                                                        } catch (err) {
                                                            notifyUser(null, "error", 'Failed to reject delete request', "toast", null, null, null, null, null, false, false, "top-center");
                                                        }
                                                    }}
                                                >
                                                    <FaTimes className="mr-1" /> Reject Delete
                                                </button>
                                                <button
                                                    className="flex items-center border border-green-600 text-green-600 px-3 py-1.5 text-sm rounded hover:bg-green-600 hover:text-white"
                                                    onClick={async () => {
                                                        try {
                                                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/account-approval/deletion-requests/${selectedApp.id}/decision`, { approve: true }, { withCredentials: true });
                                                            notifyUser(null, "success", 'Delete request approved and executed', "toast", null, null, null, null, null, false, false, "top-center");
                                                            fetchPendingApplications();
                                                            setShowModal(false);
                                                        } catch (err) {
                                                            notifyUser(null, "error", 'Failed to approve delete request', "toast", null, null, null, null, null, false, false, "top-center");
                                                        }
                                                    }}
                                                >
                                                    <FaCheck className="mr-1" /> Approve Delete
                                                </button>
                                            </>
                                        );
                                    }

                                    // Map status -> available buttons according to spec
                                    // pending: show all buttons
                                    // approved: show all except approve and reject
                                    // disabled: show delete and internal notes
                                    // deleted: show enable and internal notes
                                    // onhold: show disable, delete and internal notes
                                    // rejected: show disable, delete and internal notes

                                    const showInternal = true; // internal notes available for most statuses

                                    const showApprove = appStatus === "pending";
                                    const showReject = appStatus === "pending";
                                    const showOnHold = true; // allow putting on hold per request
                                    const showActions = appStatus === "pending" || appStatus === "approved";

                                    // Buttons for disabled/deleted/onhold/rejected cases
                                    if (isDisabled) {
                                        // disabled -> show Enable, Delete and Internal Notes
                                        return (
                                            <>
                                                <button
                                                    className="flex items-center border border-green-600 text-green-600 px-3 py-1.5 text-sm rounded hover:bg-green-600 hover:text-white"
                                                    onClick={handleEnableAccount}
                                                >
                                                    <FaCheck className="mr-1" /> Enable
                                                </button>
                                                <div className="relative">
                                                    <button
                                                        className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                        onClick={() => setShowActionsDropdown((p) => !p)}
                                                    >
                                                        <FaEdit className="mr-2" />
                                                        Actions
                                                    </button>
                                                    {showActionsDropdown && (
                                                        <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                                            <button
                                                                className="w-full text-left px-4 py-3 text-sm hover:bg-red-50 transition border-b border-gray-100 text-red-600 flex items-center"
                                                                onClick={() => { setShowActionsDropdown(false); handleDeleteAccount(); }}
                                                            >
                                                                <FaTrash className="mr-2" /> Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {showInternal && (
                                                    <button
                                                        className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                        onClick={() => setShowNoteBox(!showNoteBox)}
                                                    >
                                                        <span className="mr-1">{showNoteBox ? <FaTimes /> : <FaPlus />}</span>
                                                        Internal Notes
                                                    </button>
                                                )}
                                            </>
                                        );
                                    }

                                    if (isDeleted) {
                                        // deleted -> show Actions dropdown (with Disable) and Internal Notes
                                        return (
                                            <>
                                                <div className="relative">
                                                    <button
                                                        className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                        onClick={() => setShowActionsDropdown((p) => !p)}
                                                    >
                                                        <FaEdit className="mr-2" />
                                                        Actions
                                                    </button>
                                                    {showActionsDropdown && (
                                                        <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                                            <button
                                                                className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition text-gray-600 flex items-center"
                                                                onClick={() => { setShowActionsDropdown(false); handleDisableAccount(); }}
                                                            >
                                                                <FaPause className="mr-2" /> Disable
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {showInternal && (
                                                    <button
                                                        className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                        onClick={() => setShowNoteBox(!showNoteBox)}
                                                    >
                                                        <span className="mr-1">{showNoteBox ? <FaTimes /> : <FaPlus />}</span>
                                                        Internal Notes
                                                    </button>
                                                )}
                                            </>
                                        );
                                    }

                                    // onhold or rejected -> show disable, delete and internal notes
                                    if (appStatus === "onhold" || appStatus === "rejected") {
                                        return (
                                            <>
                                                <div className="relative">
                                                    <button
                                                        className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                        onClick={() => setShowActionsDropdown((p) => !p)}
                                                    >
                                                        <FaEdit className="mr-2" />
                                                        Actions
                                                    </button>
                                                    {showActionsDropdown && (
                                                        <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                                            <button
                                                                className="w-full text-left px-4 py-3 text-sm hover:bg-red-50 transition border-b border-gray-100 text-red-600 flex items-center"
                                                                onClick={() => { setShowActionsDropdown(false); handleDeleteAccount(); }}
                                                            >
                                                                <FaTrash className="mr-2" /> Delete
                                                            </button>
                                                            <button
                                                                className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition text-gray-600 flex items-center"
                                                                onClick={() => { handleDisableAccount(); }}
                                                            >
                                                                <FaPause className="mr-2" /> Disable
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {showInternal && (
                                                    <button
                                                        className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                        onClick={() => setShowNoteBox(!showNoteBox)}
                                                    >
                                                        <span className="mr-1">{showNoteBox ? <FaTimes /> : <FaPlus />}</span>
                                                        Internal Notes
                                                    </button>
                                                )}
                                            </>
                                        );
                                    }

                                    // approved or pending (default)
                                    return (
                                        <>
                                            <div className="relative">
                                                <button
                                                    className="aqi-application-nc-button flex items-center px-4 py-2 text-sm rounded-md"
                                                    onClick={() => setShowActionsDropdown((p) => !p)}
                                                >
                                                    <FaEdit className="mr-2" />
                                                    Actions
                                                </button>
                                                {showActionsDropdown && (
                                                    <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                                        <button
                                                            className="w-full text-left px-4 py-3 text-sm hover:bg-red-50 transition border-b border-gray-100 text-red-600 flex items-center"
                                                            onClick={() => { setShowActionsDropdown(false); handleDeleteAccount(); }}
                                                        >
                                                            <FaTrash className="mr-2" /> Delete
                                                        </button>
                                                        <button
                                                            className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition text-gray-600 flex items-center"
                                                            onClick={() => { handleDisableAccount(); }}
                                                        >
                                                            <FaPause className="mr-2" /> Disable
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {showInternal && (
                                                <button
                                                    className="flex items-center border border-gray-500 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-200"
                                                    onClick={() => setShowNoteBox(!showNoteBox)}
                                                >
                                                    <span className="mr-1">{showNoteBox ? <FaTimes /> : <FaPlus />}</span>
                                                    Internal Notes
                                                </button>
                                            )}

                                            {/* Approve/Reject/On Hold/Enable Edit/Go Live/Assign NC buttons per rules */}
                                            {showReject && (
                                                <button
                                                    className="flex items-center border border-red-600 text-red-600 px-3 py-1.5 text-sm rounded hover:bg-red-600 hover:text-white"
                                                    onClick={() => confirmAction("reject", () => setShowModal(false))}
                                                >
                                                    <FaTimes className="mr-1" /> Reject
                                                </button>
                                            )}

                                            {appStatus !== "approved" && (
                                            <button
                                                className="flex items-center border border-blue-500 text-blue-500 px-3 py-1.5 text-sm rounded hover:bg-blue-100"
                                                onClick={() => {
                                                    Swal.fire({
                                                        title: "Enable edit for this application?",
                                                        text: "The user will be able to re-edit and resubmit.",
                                                        icon: "warning",
                                                        showCancelButton: true,
                                                        confirmButtonText: "Yes, enable edit",
                                                        cancelButtonText: "Cancel",
                                                        customClass: {
                                                            confirmButton: "swal-confirm-approve",
                                                            cancelButton: "swal-cancel",
                                                            actions: "swal-actions",
                                                        },
                                                        buttonsStyling: false,
                                                    }).then(async (result) => {
                                                        if (result.isConfirmed) {
                                                            try {
                                                                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/account-approval/mark-reedit`, { userId: selectedApp.id, role: selectedApp.role }, { withCredentials: true });
                                                                notifyUser(null, "success", "Edit permission granted", "toast", null, null, null, null, null, false, false, "top-center");
                                                                fetchPendingApplications();
                                                            } catch {
                                                                notifyUser(null, "error", "Failed to enable edit", "toast", null, null, null, null, null, false, false, "top-center");
                                                            }
                                                        }
                                                    });
                                                }}
                                            >
                                                <FaEdit className="mr-1" /> Enable Edit
                                            </button>
                                            )}

                                            {showOnHold && appStatus !== "approved" && (
                                                <button
                                                    className="flex items-center border border-yellow-500 text-yellow-600 px-3 py-1.5 text-sm rounded hover:bg-yellow-100"
                                                    onClick={() => setShowOnHoldBox((prev) => !prev)}
                                                    title="Put application on hold"
                                                >
                                                    <span className="mr-1">{showOnHoldBox ? <FaTimes /> : <FaPause />}</span>
                                                    On Hold
                                                </button>
                                            )}

                                            {showApprove && (selectedApp?.role === "entrepreneur" || (selectedApp?.role === "investor" && appStatus === "pending")) && (
                                                <button
                                                    className="flex items-center border border-green-600 text-green-600 px-3 py-1.5 text-sm rounded hover:bg-green-600 hover:text-white"
                                                    onClick={() => confirmAction("approve", () => setShowModal(false))}
                                                >
                                                    <FaCheck className="mr-1" /> Approve
                                                </button>
                                            )}

                                            {/* Go Live button: show only when approved and NC record is sandbox */}
                                            {(() => {
                                                let ncStatus = modalData?.application?.nc_record_status || modalData?.details?.nc_record_status;
                                                if (!ncStatus && selectedApp?.id) {
                                                    const appRow = applications.find((a) => a.id === selectedApp.id || a.user_id === selectedApp.id || a.userId === selectedApp.id);
                                                    ncStatus = appRow?.nc_record_status || appRow?.NC_RECORD_STATUS || "";
                                                }

                                                if (selectedApp?.role === "entrepreneur" && appStatus === "approved" && ncStatus === "sandbox") {
                                                    return (
                                                        <button
                                                            className="aqi-application-nc-button flex items-center px-3 py-1.5 text-sm rounded"
                                                            onClick={handleGoLive}
                                                            title="Clicking on this submits your data to NC"
                                                        >
                                                            <FaAngleDoubleRight className="mr-1" /> Go Live
                                                        </button>
                                                    );
                                                }
                                                return null;
                                            })()}

                                        </>
                                    );
                                })()}

                                            {/* Assign NC IDs button - always visible */}
                                            <button
                                                className="aqi-application-nc-button flex items-center px-3 py-1.5 text-sm rounded"
                                                onClick={() => {
                                                    if (!showNCForm) {
                                                        if (selectedApp?.role === "investor") {
                                                            setNcPartyId(modalData?.details?.north_capital_party_id || "");
                                                            setNcAccountId(modalData?.details?.north_capital_account_id || "");
                                                        } else {
                                                            setNcIssuerId(modalData?.campaign?.north_capital_issuer_id || modalData?.application?.north_capital_issuer_id || "");
                                                            setNcOfferingId(modalData?.campaign?.north_capital_offering_id || modalData?.application?.north_capital_offering_id || "");
                                                        }
                                                    }
                                                    setShowNCForm((prev) => !prev);
                                                }}
                                            >
                                                {showNCForm ? <FaTimes className="mr-1" /> : <FaLink className="mr-1" />} {showNCForm ? "Hide" : "Assign"} NC IDs
                                            </button>
                                
                            </div>

                            {/* Persistent On Hold Note Section: displays the saved on-hold note when present */}
                            {displayedOnHoldNote && (
                                <section className="mb-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="font-semibold text-yellow-700 text-lg border-b pb-1 mb-2 tracking-wide flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-5 bg-yellow-500 rounded-sm mr-2"></span>
                                            On Hold Note
                                        </h4>
                                    </div>
                                    <div className="relative bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4 shadow-sm">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-800 font-bold text-lg shadow">
                                                <FaPause className="text-sm" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-gray-800 text-base leading-relaxed mb-1 whitespace-pre-line">
                                                    {displayedOnHoldNote}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Inline On Hold Input - toggled by the On Hold button. When open, show any existing on-hold note and the textarea to add/update feedback. */}
                            {showOnHoldBox && (
                                <div className="mb-6">
                                    <div className="rounded-xl border border-yellow-200 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-sm p-4 space-y-3">
                                        <h4 className="font-semibold text-yellow-700 mb-2 flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-5 bg-yellow-500 rounded-sm"></span>
                                            Put Application On Hold
                                        </h4>

                                        {/* If there is an existing on-hold note from the server, show it here for context */}
                                        {displayedOnHoldNote && (
                                            <div className="relative bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center text-yellow-800 font-bold text-sm">
                                                        <FaPause />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-gray-800 text-sm whitespace-pre-line">
                                                            {
                                                                displayedOnHoldNote
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <label
                                            htmlFor="on-hold-note-inline"
                                            className="block mb-2 font-medium text-sm"
                                        >
                                            Feedback for Applicant (required):
                                        </label>
                                        <textarea
                                            id="on-hold-note-inline"
                                            className="w-full border rounded p-2 mb-3"
                                            rows={4}
                                            value={onHoldNote}
                                            onChange={(e) =>
                                                setOnHoldNote(e.target.value)
                                            }
                                            placeholder="Enter feedback for the applicant..."
                                            maxLength={1000}
                                            required
                                        />
                                        <div className="flex justify-end gap-2">
                                            <button
                                                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                                disabled={!onHoldNote.trim()}
                                                onClick={() => {
                                                    handleVerification(
                                                        selectedApp.id,
                                                        selectedApp.role,
                                                        null,
                                                        null,
                                                        "",
                                                        true,
                                                        onHoldNote,
                                                    );
                                                    // Refresh application details to show the on-hold note
                                                    setTimeout(() => {
                                                        fetchApplicationDetails(
                                                            selectedApp.id,
                                                            selectedApp.role,
                                                        );
                                                    }, 500);
                                                    setShowOnHoldBox(false);
                                                }}
                                            >
                                                Save & Put On Hold
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {internalNotes.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="font-semibold text-navy text-lg border-b pb-1 mb-4 tracking-wide flex items-center gap-2">
                                        <span className="inline-block w-1.5 h-5 bg-navy rounded-sm mr-2"></span>
                                        Internal Notes
                                    </h4>
                                    <div className="space-y-4">
                                        {internalNotes.map((note, idx) => (
                                            <div
                                                key={idx}
                                                className="relative bg-gradient-to-br from-[#eaf6ff] to-[#F5F7FA] border border-[#b6d6ee] rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200 group"
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#c2d7e2] flex items-center justify-center text-navy font-bold text-lg shadow group-hover:bg-navy group-hover:text-[#c2d7e2] transition-colors duration-200">
                                                        {note.username
                                                            ? note.username
                                                                  .charAt(0)
                                                                  .toUpperCase()
                                                            : "U"}
                                                    </div>
                                                    <div className="flex-1">
                                                        {editingNoteId ===
                                                        note.id ? (
                                                            <div className="space-y-2">
                                                                <textarea
                                                                    rows="3"
                                                                    className="w-full border border-[#c2d7e2] rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition-all duration-200 bg-white resize-none"
                                                                    value={
                                                                        editingNoteText
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setEditingNoteText(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    maxLength={
                                                                        1000
                                                                    }
                                                                />
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs text-gray-400">
                                                                        {
                                                                            editingNoteText.length
                                                                        }
                                                                        /1000
                                                                    </span>
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            onClick={() =>
                                                                                handleSaveEditedNote(
                                                                                    note.id,
                                                                                )
                                                                            }
                                                                            className="bg-navy text-white px-3 py-1 rounded-lg font-semibold shadow hover:bg-navy-2 transition-colors duration-150 text-xs flex items-center gap-1"
                                                                        >
                                                                            <FaSave className="text-xs" />{" "}
                                                                            Save
                                                                        </button>
                                                                        <button
                                                                            onClick={
                                                                                handleCancelEdit
                                                                            }
                                                                            className="bg-gray-500 text-white px-3 py-1 rounded-lg font-semibold shadow hover:bg-gray-600 transition-colors duration-150 text-xs"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="flex justify-between items-start">
                                                                    <p className="text-gray-800 text-base leading-relaxed mb-1 whitespace-pre-line flex-1 pr-2">
                                                                        {
                                                                            note.note
                                                                        }
                                                                    </p>
                                                                    <button
                                                                        onClick={() =>
                                                                            handleEditNote(
                                                                                note.id,
                                                                                note.note,
                                                                            )
                                                                        }
                                                                        className="text-navy hover:text-navy-2 transition-colors duration-150 opacity-0 group-hover:opacity-100 flex-shrink-0"
                                                                        title="Edit note"
                                                                    >
                                                                        <FaEdit className="text-sm" />
                                                                    </button>
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                                                    <span className="inline-block w-1 h-1 bg-navy rounded-full"></span>
                                                                    <span className="font-medium text-navy">
                                                                        {note.username ||
                                                                            "Unknown"}
                                                                    </span>
                                                                    <span className="text-gray-400">
                                                                        on{" "}
                                                                        {new Date(
                                                                            note.created_on,
                                                                        ).toLocaleDateString()}
                                                                    </span>
                                                                    {note.updated_on &&
                                                                        note.updated_on !==
                                                                            note.created_on && (
                                                                            <span className="text-gray-400 italic">
                                                                                (edited{" "}
                                                                                {new Date(
                                                                                    note.updated_on,
                                                                                ).toLocaleDateString()}

                                                                                )
                                                                            </span>
                                                                        )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* North Capital IDs Panel */}
                            {showNCForm && (
                                <section className="mb-6">
                                    <div className="rounded-xl border border-navy bg-gradient-to-br from-[#eaf6ff] to-[#F5F7FA] shadow-sm p-4">
                                        <h4 className="font-semibold text-navy text-lg border-b pb-2 mb-4 tracking-wide flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-5 bg-navy rounded-sm mr-2"></span>
                                            Assign North Capital IDs
                                        </h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedApp?.role === "investor" ? (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-navy mb-2">
                                                            Party ID
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={ncPartyId}
                                                            onChange={(e) => setNcPartyId(e.target.value)}
                                                            placeholder="Enter Party ID"
                                                            className="w-full px-3 py-2 border border-navy rounded-lg text-sm bg-white text-navy focus:outline-none focus:ring-2 focus:ring-navy"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-navy mb-2">
                                                            Account ID
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={ncAccountId}
                                                            onChange={(e) => setNcAccountId(e.target.value)}
                                                            placeholder="Enter Account ID"
                                                            className="w-full px-3 py-2 border border-navy rounded-lg text-sm bg-white text-navy focus:outline-none focus:ring-2 focus:ring-navy"
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-navy mb-2">
                                                            Issuer ID
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={ncIssuerId}
                                                            onChange={(e) => setNcIssuerId(e.target.value)}
                                                            placeholder="Enter Issuer ID"
                                                            className="w-full px-3 py-2 border border-navy rounded-lg text-sm bg-white text-navy focus:outline-none focus:ring-2 focus:ring-navy"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-navy mb-2">
                                                            Offering ID
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={ncOfferingId}
                                                            onChange={(e) => setNcOfferingId(e.target.value)}
                                                            placeholder="Enter Offering ID"
                                                            className="w-full px-3 py-2 border border-navy rounded-lg text-sm bg-white text-navy focus:outline-none focus:ring-2 focus:ring-navy"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex justify-end mt-4">
                                            <button
                                                onClick={handleAssignNCIds}
                                                disabled={ncSaving}
                                                className="aqi-primary-action flex items-center gap-1 rounded-lg bg-navy px-5 py-2 text-sm font-semibold text-white shadow transition-colors duration-150 hover:bg-navy-2 disabled:opacity-60"
                                            >
                                                <FaSave className="text-xs" />
                                                {ncSaving ? "Saving..." : "Save NC IDs"}
                                            </button>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Text area for the internal notes */}
                            {showNoteBox && (
                                <div className="mb-6">
                                    <div className="rounded-xl border border-[#b6d6ee] bg-gradient-to-br from-[#F5F7FA] to-[#eaf6ff] shadow-sm p-4">
                                        <label
                                            htmlFor="internal-note-textarea"
                                            className="block text-navy font-semibold mb-2 text-base tracking-wide"
                                        >
                                            Add Internal Note
                                        </label>
                                        <textarea
                                            id="internal-note-textarea"
                                            rows="4"
                                            placeholder="Enter your internal note here..."
                                            className="w-full border border-[#c2d7e2] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition-all duration-200 bg-white resize-none shadow-inner"
                                            value={internalNote}
                                            onChange={(e) =>
                                                setInternalNote(e.target.value)
                                            }
                                            maxLength={1000}
                                        />
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-xs text-gray-400">
                                                {internalNote.length}/1000
                                            </span>
                                            <button
                                                onClick={
                                                    handleSubmitInternalNote
                                                }
                                                className="aqi-primary-action flex items-center gap-1 rounded-lg bg-navy px-5 py-2 text-sm font-semibold text-white shadow transition-colors duration-150 hover:bg-navy-2"
                                            >
                                                Save Note
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* User Info */}
                            <section className="mb-6">
                                <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2 font-poppins">
                                    User Info
                                </h4>
                                <p>
                                    <strong>Name:</strong>{" "}
                                    {modalData.user.first_name}{" "}
                                    {modalData.user.last_name}
                                </p>
                                <p>
                                    <strong>Email:</strong>{" "}
                                    {modalData.user.email}
                                </p>
                                <p>
                                    <strong>Role:</strong> {selectedApp?.role}
                                </p>
                                <p>
                                    <strong>Phone Number:</strong>{" "}
                                    {modalData.user.phone_number}
                                </p>
                            </section>

                            {/* NC Record Status Section - for both investors and entrepreneurs */}
                            <section className="mb-6">
                                <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                    NC Record Status
                                </h4>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <p className="text-gray-600 text-sm">
                                        <strong>Status:</strong>{" "}
                                        {(() => {
                                            // Prefer modalData details/application, fallback to applications list
                                            let ncStatus = null;
                                            if (selectedApp?.role === "investor") {
                                                ncStatus = modalData?.details?.nc_record_status;
                                            } else {
                                                ncStatus = modalData?.application?.nc_record_status;
                                            }

                                            if (!ncStatus && selectedApp?.id) {
                                                const appRow = applications.find((a) => a.id === selectedApp.id || a.user_id === selectedApp.id || a.userId === selectedApp.id);
                                                ncStatus = appRow?.nc_record_status || appRow?.NC_RECORD_STATUS || "";
                                            }

                                            return ncStatus || "-";
                                        })()}
                                    </p>
                                </div>
                            </section>
                                 

                            {/* Role Info */}
                            <section className="mb-6">
                                <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                    {selectedApp?.role === "investor"
                                        ? "Investor Details"
                                        : "Entrepreneur Details"}
                                </h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    {Object.entries(
                                        selectedApp?.role === "investor"
                                            ? modalData.details || {}
                                            : {
                                                  ...modalData.entrepreneur,
                                                  ...modalData.application,
                                                  ...modalData.campaign,
                                              },
                                    ).map(([key, value]) => {

                                        if(
                                            key.includes("file") ||
                                            key.includes("document") ||
                                            key.includes("logo") ||
                                            key === "signature_path"
                                        )
                                            return null;
                                        let displayValue = String(value);

                                        // ── Date handler ──
                                        if(
                                            key.includes("date") ||
                                            key.includes("created") ||
                                            key.includes("updated") ||
                                            key.includes("time") ||
                                            (typeof value === "string" &&
                                                value.match(
                                                    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,
                                                ))
                                        ) {
                                            try {
                                                // Plain YYYY-MM-DD strings (e.g. birthdate) must be
                                                // parsed as local date parts to avoid UTC-midnight
                                                // timezone shift (e.g. "1975-02-28" → Feb 27 in US).
                                                const plainDate = typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}$/);
                                                let date;
                                                if(plainDate) {
                                                    const [y, m, d] = value.split("-").map(Number);
                                                    date = new Date(y, m - 1, d);
                                                } else {
                                                    date = new Date(value);
                                                }
                                                if(!isNaN(date.getTime())) {
                                                    displayValue =
                                                        date.toLocaleDateString();
                                                }
                                            } catch{
                                                // Keep original value if date parsing fails
                                            }
                                        }

                                        // ── SSN handler ──
                                        if(
                                            key === "encrypted_ssn" ||
                                            key === "ssn_iv" ||
                                            key === "ssn_key_index"
                                        )
                                            return null;

                                        if(key === "ssn_masked") {
                                            return (
                                                <div key={key}>
                                                    <strong>SSN:</strong>{" "}
                                                    {value}
                                                </div>
                                            );
                                        }
                                        //  EIN handler
                                        if(
                                            key.toLowerCase() === "ein" &&
                                            typeof value === "string" &&
                                            selectedApp?.role === "entrepreneur"
                                        ) {
                                            const last4 = value.slice(-4);
                                            const masked =
                                                "*".repeat(value.length - 4) +
                                                last4;
                                            displayValue = masked;
                                        }

                                        // Board members: show as text if array/object
                                        if(
                                            key.toLowerCase().includes("board")
                                        ) {
                                            let members = value;
                                            // If value is a stringified JSON, parse it
                                            if(typeof members === "string") {
                                                try {
                                                    members =
                                                        JSON.parse(members);
                                                } catch{
                                                    // Not JSON, show as is
                                                }
                                            }
                                            // If array, show as a list with headings
                                            if(Array.isArray(members)) {
                                                return (
                                                    <div key={key}>
                                                        <strong className="capitalize">
                                                            {key.replace(
                                                                /_/g,
                                                                " ",
                                                            )}
                                                            :
                                                        </strong>
                                                        {members.length > 0 ? (
                                                            <ul className="ml-4 mt-1 list-disc">
                                                                {members.map(
                                                                    (
                                                                        member,
                                                                        idx,
                                                                    ) => {
                                                                        if(
                                                                            typeof member ===
                                                                                "object" &&
                                                                            member !==
                                                                                null
                                                                        ) {
                                                                            return (
                                                                                <li
                                                                                    key={
                                                                                        idx
                                                                                    }
                                                                                    className="mb-1"
                                                                                >
                                                                                    <div>
                                                                                        <strong>
                                                                                            Name:
                                                                                        </strong>{" "}
                                                                                        {member.name ||
                                                                                            "-"}
                                                                                    </div>
                                                                                    <div>
                                                                                        <strong>
                                                                                            Title:
                                                                                        </strong>{" "}
                                                                                        {member.title ||
                                                                                            "-"}
                                                                                    </div>
                                                                                </li>
                                                                            );
                                                                        }
                                                                        // If string, just show as name
                                                                        return (
                                                                            <li
                                                                                key={
                                                                                    idx
                                                                                }
                                                                                className="mb-1"
                                                                            >
                                                                                <div>
                                                                                    <strong>
                                                                                        Name:
                                                                                    </strong>{" "}
                                                                                    {
                                                                                        member
                                                                                    }
                                                                                </div>
                                                                            </li>
                                                                        );
                                                                    },
                                                                )}
                                                            </ul>
                                                        ) : (
                                                            " None"
                                                        )}
                                                    </div>
                                                );
                                            }
                                            // If object, join values as list
                                            if(
                                                typeof members === "object" &&
                                                members !== null
                                            ) {
                                                return (
                                                    <div key={key}>
                                                        <strong className="capitalize">
                                                            {key.replace(
                                                                /_/g,
                                                                " ",
                                                            )}
                                                            :
                                                        </strong>
                                                        <ul className="ml-4 mt-1 list-disc">
                                                            {Object.values(
                                                                members,
                                                            ).map(
                                                                (
                                                                    member,
                                                                    idx,
                                                                ) => {
                                                                    if(
                                                                        typeof member ===
                                                                            "object" &&
                                                                        member !==
                                                                            null
                                                                    ) {
                                                                        return (
                                                                            <li
                                                                                key={
                                                                                    idx
                                                                                }
                                                                                className="mb-1"
                                                                            >
                                                                                <div>
                                                                                    <strong>
                                                                                        Name:
                                                                                    </strong>{" "}
                                                                                    {member.name ||
                                                                                        "-"}
                                                                                </div>
                                                                                <div>
                                                                                    <strong>
                                                                                        Title:
                                                                                    </strong>{" "}
                                                                                    {member.title ||
                                                                                        "-"}
                                                                                </div>
                                                                            </li>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <li
                                                                            key={
                                                                                idx
                                                                            }
                                                                            className="mb-1"
                                                                        >
                                                                            <div>
                                                                                <strong>
                                                                                    Name:
                                                                                </strong>{" "}
                                                                                {
                                                                                    member
                                                                                }
                                                                            </div>
                                                                        </li>
                                                                    );
                                                                },
                                                            )}
                                                        </ul>
                                                    </div>
                                                );
                                            }
                                            // Fallback: show as string
                                            return (
                                                <div key={key}>
                                                    <strong className="capitalize">
                                                        {key.replace(/_/g, " ")}
                                                        :
                                                    </strong>{" "}
                                                    {String(value)}
                                                </div>
                                            );
                                        }

                                        // Handle social links as clickable links
                                        if(
                                            key === "formatted_social_links" &&
                                            value
                                        ) {
                                            return (
                                                <div key={key}>
                                                    <strong className="capitalize">
                                                        Social Links:
                                                    </strong>{" "}
                                                    {value
                                                        .split(", ")
                                                        .map((link, i) => (
                                                            <React.Fragment
                                                                key={i}
                                                            >
                                                                {i > 0 && ", "}
                                                                <a
                                                                    href={link}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="text-blue-600 hover:underline"
                                                                >
                                                                    {link.replace(
                                                                        /(^\w+:|^)\/\//,
                                                                        "",
                                                                    )}
                                                                </a>
                                                            </React.Fragment>
                                                        ))}
                                                </div>
                                            );
                                        }

                                        // Skip displaying raw social_links as they're now formatted
                                        if(key === "social_links") {
                                            return null;
                                        }

                                        // Make LinkedIn URL clickable
                                        if(
                                            key === "linkedin" &&
                                            value &&
                                            typeof value === "string" &&
                                            value.trim() !== ""
                                        ) {
                                            const linkedinUrl =
                                                value.startsWith("http")
                                                    ? value
                                                    : `https://${value}`;
                                            return (
                                                <div key={key}>
                                                    <strong className="capitalize">
                                                        LinkedIn:
                                                    </strong>{" "}
                                                    <a
                                                        href={linkedinUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {value.replace(
                                                            /(^\w+:|^)\/\//,
                                                            "",
                                                        )}
                                                    </a>
                                                </div>
                                            );
                                        }

                                        // Make company website clickable
                                        if(
                                            key === "company_website" &&
                                            value &&
                                            typeof value === "string" &&
                                            value.trim() !== ""
                                        ) {
                                            const websiteUrl = value.startsWith(
                                                "http",
                                            )
                                                ? value
                                                : `https://${value}`;
                                            return (
                                                <div key={key}>
                                                    <strong className="capitalize">
                                                        Company Website:
                                                    </strong>{" "}
                                                    <a
                                                        href={websiteUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {value.replace(
                                                            /(^\w+:|^)\/\//,
                                                            "",
                                                        )}
                                                    </a>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={key}>
                                                <strong className="capitalize">
                                                    {key.toLowerCase() === "ein"
                                                        ? "EIN"
                                                        : key.replace(
                                                              /_/g,
                                                              " ",
                                                          )}
                                                    :
                                                </strong>{" "}
                                                {displayValue}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Signature preview at the bottom */}
                                {(() => {
                                    // grab path for whichever role is open
                                    const sigPath =
                                        selectedApp?.role === "investor"
                                            ? modalData.details?.signature_path
                                            : modalData.application
                                                  ?.signature_path;

                                    if(!sigPath) return null;

                                    const url = signatureUrl(sigPath);
                                    return (
                                        <section className="mb-6 mt-4">
                                            <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                                Signature
                                            </h4>
                                            <img
                                                src={url}
                                                alt="User Signature"
                                                className="h-32 border rounded shadow"
                                            />
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="ml-3 text-navy underline text-xs align-middle"
                                            >
                                                Open
                                            </a>
                                        </section>
                                    );
                                })()}
                            </section>

                            <section>
                                <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                    Upload
                                </h4>

                                {(() => {
                                    const companyLogoUrl =
                                        resolveCompanyLogoUrl(
                                            modalData?.application,
                                            modalData?.user?.id,
                                        );

                                    if(!companyLogoUrl) return null;

                                    return (
                                        <section className="mb-6">
                                            <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                                Company Logo
                                            </h4>
                                            <div className="flex flex-wrap gap-4">
                                                <div className="border p-2 rounded bg-gray-100 w-44">
                                                    <img
                                                        src={companyLogoUrl}
                                                        alt="Company Logo"
                                                        className="h-24 w-full object-contain rounded mb-1"
                                                    />
                                                    <a
                                                        href={companyLogoUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-navy underline text-xs"
                                                    >
                                                        Open
                                                    </a>
                                                </div>
                                            </div>
                                        </section>
                                    );
                                })()}

                                {(modalData?.application?.investment_logo ||
                                    modalData?.campaign?.investment_logo) && (
                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                            Investment Logo
                                        </h4>
                                        <div className="flex flex-wrap gap-4">
                                            <div className="border p-2 rounded bg-gray-100 w-44">
                                                <img
                                                    src={(() => {
                                                        const investmentLogoUrl =
                                                            modalData?.application?.investment_logo_url ||
                                                            modalData?.campaign?.investment_logo_url;
                                                        const investmentLogo =
                                                            modalData?.application?.investment_logo ||
                                                            modalData?.campaign?.investment_logo;

                                                        if (investmentLogoUrl) return investmentLogoUrl;
                                                        if (!investmentLogo) return "";

                                                        // If already absolute URL
                                                        if (investmentLogo.startsWith("http://") || investmentLogo.startsWith("https://")) {
                                                            return investmentLogo;
                                                        }

                                                        // If starts with /uploads/ or uploads/
                                                        if (investmentLogo.startsWith("/uploads/")) {
                                                            return `${process.env.REACT_APP_BACKEND_URL}${investmentLogo}`;
                                                        }
                                                        if (investmentLogo.startsWith("uploads/")) {
                                                            return `${process.env.REACT_APP_BACKEND_URL}/${investmentLogo}`;
                                                        }

                                                        // If contains a folder-like path (e.g., userId/investment_logo/filename)
                                                        if (investmentLogo.includes("/")) {
                                                            return `${process.env.REACT_APP_BACKEND_URL}/uploads/${investmentLogo}`;
                                                        }

                                                        // Fallback: assume user folder under investment_logo (use underscore folder name)
                                                        return `${process.env.REACT_APP_BACKEND_URL}/uploads/${modalData.user.id}/investment_logo/${investmentLogo}`;
                                                    })()}
                                                    alt="Investment Logo Preview"
                                                    className="h-24 w-full object-cover rounded mb-1"
                                                />
                                                <a
                                                    href={(() => {
                                                        const investmentLogoUrl =
                                                            modalData?.application?.investment_logo_url ||
                                                            modalData?.campaign?.investment_logo_url;
                                                        const investmentLogo =
                                                            modalData?.application?.investment_logo ||
                                                            modalData?.campaign?.investment_logo;

                                                        if (investmentLogoUrl) return investmentLogoUrl;
                                                        if (!investmentLogo) return "#";

                                                        if (investmentLogo.startsWith("http://") || investmentLogo.startsWith("https://")) {
                                                            return investmentLogo;
                                                        }
                                                        if (investmentLogo.startsWith("/uploads/")) {
                                                            return `${process.env.REACT_APP_BACKEND_URL}${investmentLogo}`;
                                                        }
                                                        if (investmentLogo.startsWith("uploads/")) {
                                                            return `${process.env.REACT_APP_BACKEND_URL}/${investmentLogo}`;
                                                        }
                                                        if (investmentLogo.includes("/")) {
                                                            return `${process.env.REACT_APP_BACKEND_URL}/uploads/${investmentLogo}`;
                                                        }
                                                        return `${process.env.REACT_APP_BACKEND_URL}/uploads/${modalData.user.id}/investment_logo/${investmentLogo}`;
                                                    })()}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-navy underline text-xs"
                                                >
                                                    Open
                                                </a>
                                            </div>
                                        </div>
                                    </section>
                                )}

                                {(() => {
                                    let filesByType = {};
                                    try {
                                        const raw =
                                            modalData?.application
                                                ?.uploaded_files;
                                        filesByType =
                                            typeof raw === "string"
                                                ? JSON.parse(raw)
                                                : raw;
                                        if(
                                            typeof filesByType !== "object" ||
                                            filesByType === null
                                        )
                                            filesByType = {};
                                    } catch{
                                        filesByType = {};
                                    }

                                    return Object.entries(filesByType).map(
                                        ([docType, files], i) => {
                                            const isArray =
                                                Array.isArray(files);
                                            const fileList = isArray
                                                ? files
                                                : [files];

                                            // Filter out invalid files
                                            const validFileList =
                                                fileList.filter(
                                                    (file) =>
                                                        file &&
                                                        typeof file ===
                                                            "string",
                                                );

                                            return (
                                                <div key={i} className="mb-4">
                                                    <h5 className="text-sm font-semibold text-navy capitalize mb-2">
                                                        {docType.replace(
                                                            /([A-Z])/g,
                                                            " $1",
                                                        )}
                                                    </h5>
                                                    <div className="flex flex-wrap gap-4">
                                                        {validFileList.map(
                                                            (file, index) => {
                                                                // Check if file already contains full path (starts with 'uploads/')
                                                                const fileUrl =
                                                                    file.startsWith(
                                                                        "uploads/",
                                                                    )
                                                                        ? `${process.env.REACT_APP_BACKEND_URL}/${file}`
                                                                        : `${process.env.REACT_APP_BACKEND_URL}/uploads/${modalData.user.id}/${docType}/${file}`;
                                                                const ext = file
                                                                    .split("/")
                                                                    .pop()
                                                                    .split(".")
                                                                    .pop()
                                                                    .toLowerCase();

                                                                return (
                                                                    <div
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="border p-2 rounded bg-gray-100 w-44 cursor-pointer"
                                                                        onClick={() => {
                                                                            console.log(
                                                                                "Opening preview:",
                                                                                {
                                                                                    fileUrl,
                                                                                    ext,
                                                                                },
                                                                            );
                                                                            setPreviewFileUrl(
                                                                                fileUrl,
                                                                            );
                                                                            setPreviewFileType(
                                                                                ext,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <p className="truncate text-xs mb-1">
                                                                            {
                                                                                file
                                                                            }
                                                                        </p>
                                                                        {[
                                                                            "jpg",
                                                                            "jpeg",
                                                                            "png",
                                                                            "gif",
                                                                            "webp",
                                                                        ].includes(
                                                                            ext,
                                                                        ) ? (
                                                                            <img
                                                                                src={
                                                                                    fileUrl
                                                                                }
                                                                                alt="Preview"
                                                                                className="h-24 w-full object-cover rounded mb-1"
                                                                            />
                                                                        ) : [
                                                                              "pdf",
                                                                              "doc",
                                                                              "docx",
                                                                              "ppt",
                                                                              "pptx",
                                                                          ].includes(
                                                                              ext,
                                                                          ) ? (
                                                                            <div className="flex flex-col items-center justify-center h-16 mb-1">
                                                                                {ext ===
                                                                                    "pdf" && (
                                                                                    <span
                                                                                        role="img"
                                                                                        aria-label="PDF"
                                                                                        className="text-3xl"
                                                                                    >
                                                                                        📄
                                                                                    </span>
                                                                                )}
                                                                                {(ext ===
                                                                                    "doc" ||
                                                                                    ext ===
                                                                                        "docx") && (
                                                                                    <span
                                                                                        role="img"
                                                                                        aria-label="DOCX"
                                                                                        className="text-3xl"
                                                                                    >
                                                                                        📝
                                                                                    </span>
                                                                                )}
                                                                                {(ext ===
                                                                                    "ppt" ||
                                                                                    ext ===
                                                                                        "pptx") && (
                                                                                    <span
                                                                                        role="img"
                                                                                        aria-label="PPTX"
                                                                                        className="text-3xl"
                                                                                    >
                                                                                        📊
                                                                                    </span>
                                                                                )}
                                                                                <span className="text-xs mt-1">
                                                                                    {ext.toUpperCase()}{" "}
                                                                                    file
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="text-navy underline text-xs">
                                                                                Click
                                                                                to
                                                                                Preview
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        },
                                    );
                                })()}

                                {/* Media Uploads Section */}
                                {modalData?.campaign?.media_uploads && (
                                    <section className="mb-6">
                                        <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                            Media Uploads
                                        </h4>
                                        <div className="flex flex-wrap gap-4">
                                            {(typeof modalData.campaign
                                                .media_uploads === "string"
                                                ? JSON.parse(
                                                      modalData.campaign
                                                          .media_uploads,
                                                  )
                                                : modalData.campaign
                                                      .media_uploads
                                            )
                                                .filter(
                                                    (file) =>
                                                        file &&
                                                        typeof file ===
                                                            "string",
                                                )
                                                .map((file, index) => {
                                                    // Check if file already contains full path (starts with 'uploads/')
                                                    const fileUrl =
                                                        file.startsWith(
                                                            "uploads/",
                                                        )
                                                            ? `${process.env.REACT_APP_BACKEND_URL}/${file}`
                                                            : `${process.env.REACT_APP_BACKEND_URL}/uploads/${modalData.user.id}/media/${file}`;
                                                    const ext = file
                                                        .split("/")
                                                        .pop()
                                                        .split(".")
                                                        .pop()
                                                        .toLowerCase();
                                                    return (
                                                        <div
                                                            key={index}
                                                            className="border p-2 rounded bg-gray-100 w-44"
                                                        >
                                                            <p className="truncate text-xs mb-1">
                                                                {file}
                                                            </p>
                                                            {[
                                                                "jpg",
                                                                "jpeg",
                                                                "png",
                                                                "gif",
                                                            ].includes(ext) && (
                                                                <img
                                                                    src={
                                                                        fileUrl
                                                                    }
                                                                    className="h-24 w-full object-cover rounded mb-1"
                                                                />
                                                            )}
                                                            {[
                                                                "mp3",
                                                                "wav",
                                                            ].includes(ext) && (
                                                                <audio
                                                                    controls
                                                                    className="w-full mb-1"
                                                                >
                                                                    <source
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                        type={`audio/${ext}`}
                                                                    />
                                                                </audio>
                                                            )}
                                                            {[
                                                                "mp4",
                                                                "webm",
                                                            ].includes(ext) && (
                                                                <video
                                                                    controls
                                                                    className="w-full mb-1 h-24 object-cover rounded"
                                                                >
                                                                    <source
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                        type={`video/${ext}`}
                                                                    />
                                                                </video>
                                                            )}
                                                            {[
                                                                "pdf",
                                                                "doc",
                                                                "docx",
                                                                "ppt",
                                                                "pptx",
                                                            ].includes(ext) && (
                                                                <div className="flex flex-col items-center justify-center h-24 mb-1">
                                                                    {ext ===
                                                                        "pdf" && (
                                                                        <span
                                                                            role="img"
                                                                            aria-label="PDF"
                                                                            className="text-4xl"
                                                                        >
                                                                            📄
                                                                        </span>
                                                                    )}
                                                                    {(ext ===
                                                                        "doc" ||
                                                                        ext ===
                                                                            "docx") && (
                                                                        <span
                                                                            role="img"
                                                                            aria-label="DOCX"
                                                                            className="text-4xl"
                                                                        >
                                                                            📝
                                                                        </span>
                                                                    )}
                                                                    {(ext ===
                                                                        "ppt" ||
                                                                        ext ===
                                                                            "pptx") && (
                                                                        <span
                                                                            role="img"
                                                                            aria-label="PPTX"
                                                                            className="text-4xl"
                                                                        >
                                                                            📊
                                                                        </span>
                                                                    )}
                                                                    <span className="text-xs mt-1">
                                                                        {ext.toUpperCase()}{" "}
                                                                        file
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <a
                                                                href={fileUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-navy underline text-xs"
                                                            >
                                                                Open
                                                            </a>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </section>
                                )}

                                {/* Video URL Embed */}
                                {selectedApp?.role === "entrepreneur" &&
                                    modalData?.campaign?.video_url && (
                                        <div className="mt-4">
                                            <h4 className="font-medium text-navy text-lg border-b pb-1 mb-2">
                                                Video
                                            </h4>
                                            <div className="aspect-w-16 aspect-h-9">
                                                <iframe
                                                    src={getEmbeddedUrl(
                                                        modalData.campaign
                                                            .video_url,
                                                    )}
                                                    title="Campaign Video"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    className="w-full h-64 rounded"
                                                ></iframe>
                                            </div>
                                        </div>
                                    )}
                            </section>

                            <button
                                className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-xl"
                                onClick={() => setShowModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}

                {/* Additional Uploads Modal */}
                {showAdditionalModal && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-lg w-11/12 max-w-3xl p-6 relative max-h-[90vh] overflow-y-auto">
                            <h3 className="text-xl font-bold mb-4 text-navy">
                                Additional Uploads
                            </h3>

                            {additionalFiles.length === 0 ? (
                                <p className="text-center text-gray-500 italic">
                                    No additional uploads to approve at this
                                    moment.
                                </p>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        {additionalFiles.map((file, index) => {
                                            const filePath = file;
                                            const fileUrl = filePath.startsWith(
                                                "/uploads/",
                                            )
                                                ? `${process.env.REACT_APP_BACKEND_URL}${filePath}`
                                                : filePath.startsWith(
                                                        "uploads/",
                                                    )
                                                  ? `${process.env.REACT_APP_BACKEND_URL}/${filePath}`
                                                  : `${process.env.REACT_APP_BACKEND_URL}/uploads/${selectedAdditionalUserId}/additional-documents/${filePath}`;
                                            const extension = filePath
                                                .split(".")
                                                .pop()
                                                .toLowerCase();
                                            return (
                                                <div
                                                    key={index}
                                                    className="border p-2 rounded bg-gray-100 w-44"
                                                >
                                                    <p className="truncate text-xs mb-1">
                                                        {filePath
                                                            .split("/")
                                                            .pop()}
                                                    </p>
                                                    {[
                                                        "jpg",
                                                        "jpeg",
                                                        "png",
                                                        "gif",
                                                        "webp",
                                                    ].includes(extension) && (
                                                        <img
                                                            src={fileUrl}
                                                            alt="Preview"
                                                            className="h-24 w-full object-cover rounded mb-1"
                                                        />
                                                    )}
                                                    {[
                                                        "mp3",
                                                        "wav",
                                                        "ogg",
                                                    ].includes(extension) && (
                                                        <audio
                                                            controls
                                                            className="w-full mb-1"
                                                        >
                                                            <source
                                                                src={fileUrl}
                                                                type={`audio/${extension}`}
                                                            />
                                                        </audio>
                                                    )}
                                                    {[
                                                        "mp4",
                                                        "webm",
                                                        "mov",
                                                    ].includes(extension) && (
                                                        <video
                                                            controls
                                                            className="w-full mb-1 h-24 object-cover rounded"
                                                        >
                                                            <source
                                                                src={fileUrl}
                                                                type={`video/${extension}`}
                                                            />
                                                        </video>
                                                    )}
                                                    {[
                                                        "pdf",
                                                        "doc",
                                                        "docx",
                                                        "ppt",
                                                        "pptx",
                                                    ].includes(extension) && (
                                                        <div className="flex flex-col items-center justify-center h-24 mb-1">
                                                            {extension ===
                                                                "pdf" && (
                                                                <span
                                                                    role="img"
                                                                    aria-label="PDF"
                                                                    className="text-4xl"
                                                                >
                                                                    📄
                                                                </span>
                                                            )}
                                                            {(extension ===
                                                                "doc" ||
                                                                extension ===
                                                                    "docx") && (
                                                                <span
                                                                    role="img"
                                                                    aria-label="DOCX"
                                                                    className="text-4xl"
                                                                >
                                                                    📝
                                                                </span>
                                                            )}
                                                            {(extension ===
                                                                "ppt" ||
                                                                extension ===
                                                                    "pptx") && (
                                                                <span
                                                                    role="img"
                                                                    aria-label="PPTX"
                                                                    className="text-4xl"
                                                                >
                                                                    📊
                                                                </span>
                                                            )}
                                                            <span className="text-xs mt-1">
                                                                {extension.toUpperCase()}{" "}
                                                                file
                                                            </span>
                                                        </div>
                                                    )}
                                                    <a
                                                        href={fileUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-600 underline text-xs"
                                                    >
                                                        Open
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex justify-end mt-4 gap-2">
                                        <button
                                            className="px-4 py-2 bg-navy text-white rounded hover:bg-[#003656]"
                                            onClick={() =>
                                                approveAdditionalUploads(
                                                    selectedAdditionalUserId,
                                                )
                                            }
                                        >
                                            Approve All
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                                            onClick={rejectAdditionalUploads}
                                        >
                                            Reject All
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Close Modal Button */}
                            <button
                                className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-xl"
                                onClick={() => setShowAdditionalModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {previewFileUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg w-11/12 max-w-5xl relative max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4 text-navy">
                            File Preview
                        </h3>

                        {/* Preview content based on file type */}
                        {["jpg", "jpeg", "png", "gif", "webp"].includes(
                            previewFileType,
                        ) && (
                            <div className="text-center">
                                <img
                                    src={previewFileUrl}
                                    alt="Preview"
                                    className="max-w-full max-h-[80vh] mx-auto rounded shadow"
                                    onError={(e) => {
                                        console.log(
                                            "Image failed to load:",
                                            previewFileUrl,
                                        );
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                            "block";
                                    }}
                                />
                                <div
                                    style={{ display: "none" }}
                                    className="text-center text-gray-600"
                                >
                                    <p>Failed to load image preview.</p>
                                    <a
                                        href={previewFileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 underline mt-2 inline-block"
                                    >
                                        Open image in new tab
                                    </a>
                                </div>
                            </div>
                        )}
                        {["pdf"].includes(previewFileType) && (
                            <div>
                                <iframe
                                    src={previewFileUrl}
                                    className="w-full h-[80vh] rounded border"
                                    title="Document Preview"
                                    onError={() => {
                                        console.log(
                                            "PDF iframe failed to load, trying Google Docs viewer",
                                        );
                                    }}
                                />
                                <div className="text-center mt-2">
                                    <a
                                        href={previewFileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 underline"
                                    >
                                        Open PDF in new tab
                                    </a>
                                </div>
                            </div>
                        )}
                        {["doc", "docx", "txt"].includes(previewFileType) && (
                            <div className="text-center text-gray-600">
                                <p>No preview available for this file type.</p>
                                <a
                                    href={previewFileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 underline mt-2 inline-block"
                                >
                                    Click here to download and view
                                </a>
                            </div>
                        )}

                        {["mp3", "wav", "ogg"].includes(previewFileType) && (
                            <audio controls className="w-full">
                                <source
                                    src={previewFileUrl}
                                    type={`audio/${previewFileType}`}
                                />
                            </audio>
                        )}
                        {["mp4", "webm", "mov"].includes(previewFileType) && (
                            <video
                                controls
                                className="w-full max-h-[80vh] rounded"
                            >
                                <source
                                    src={previewFileUrl}
                                    type={`video/${previewFileType}`}
                                />
                            </video>
                        )}

                        <button
                            onClick={() => {
                                setPreviewFileUrl(null);
                                setPreviewFileType(null);
                            }}
                            className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-xl"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

AccountApprovalDashboard.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};

export default AccountApprovalDashboard;
